import React, { useState, useEffect } from "react";

const Payment = ({ payment = {}, setPayment }) => {
    const [paymentSettings, setPaymentSettings] = useState({
        // General Payment Settings
        currency: 'INR',
        currency_symbol: '₹',
        payment_mode: 'both', // 'test', 'live', 'both'
        auto_capture: true,
        refund_enabled: true,
        partial_refund: true,
        payment_timeout: 15, // minutes
        
        // Indian Payment Gateways
        razorpay_enabled: false,
        razorpay_key_id: '',
        razorpay_key_secret: '',
        razorpay_webhook_secret: '',
        
        payu_enabled: false,
        payu_merchant_key: '',
        payu_merchant_salt: '',
        payu_auth_header: '',
        
        paytm_enabled: false,
        paytm_merchant_id: '',
        paytm_merchant_key: '',
        paytm_website: '',
        paytm_industry_type: '',
        paytm_channel_id: '',
        
        ccavenue_enabled: false,
        ccavenue_merchant_id: '',
        ccavenue_access_code: '',
        ccavenue_working_key: '',
        
        instamojo_enabled: false,
        instamojo_api_key: '',
        instamojo_auth_token: '',
        instamojo_salt: '',
        
        // International Payment Gateways
        stripe_enabled: false,
        stripe_publishable_key: '',
        stripe_secret_key: '',
        stripe_webhook_secret: '',
        
        paypal_enabled: false,
        paypal_client_id: '',
        paypal_client_secret: '',
        paypal_mode: 'sandbox', // 'sandbox' or 'live'
        
        square_enabled: false,
        square_application_id: '',
        square_access_token: '',
        square_location_id: '',
        
        // UPI and Digital Wallets
        upi_enabled: true,
        google_pay_enabled: true,
        phonepe_enabled: true,
        paytm_wallet_enabled: true,
        amazon_pay_enabled: false,
        amazon_pay_merchant_id: '',
        amazon_pay_access_key: '',
        amazon_pay_secret_key: '',
        
        // Bank Transfer
        bank_transfer_enabled: true,
        bank_name: '',
        bank_account_number: '',
        bank_ifsc_code: '',
        bank_account_holder: '',
        
        // Cash on Delivery
        cod_enabled: true,
        cod_minimum_amount: 0,
        cod_maximum_amount: 10000,
        
        // Payment Methods Configuration
        credit_card_enabled: true,
        debit_card_enabled: true,
        net_banking_enabled: true,
        wallet_enabled: true,
        emi_enabled: false,
        
        // Fee Configuration
        processing_fee_enabled: false,
        processing_fee_type: 'percentage', // 'percentage' or 'fixed'
        processing_fee_value: 2.5, // percentage or fixed amount
        processing_fee_minimum: 0,
        processing_fee_maximum: 100,
        
        // Tax Configuration
        gst_enabled: true,
        gst_rate: 18, // percentage
        cgst_rate: 9,
        sgst_rate: 9,
        igst_rate: 18,
        
        // Notification Settings
        payment_success_email: true,
        payment_failure_email: true,
        payment_success_sms: true,
        payment_failure_sms: true,
        admin_notification: true,
        
        // Security Settings
        fraud_detection: true,
        ip_whitelist: '',
        card_verification: true,
        address_verification: false,
        
        // Refund Settings
        auto_refund: false,
        refund_processing_days: 7,
        refund_reason_required: true,
        
        // Settlement Settings
        settlement_frequency: 'daily', // 'daily', 'weekly', 'monthly'
        settlement_day: 1, // 1-31 for monthly, 1-7 for weekly
        settlement_time: '09:00',
        
        // Analytics and Reporting
        payment_analytics: true,
        transaction_logging: true,
        detailed_reporting: true,
        export_enabled: true
    });

    useEffect(() => {
        if (payment && Object.keys(payment).length > 0) {
            setPaymentSettings(prev => ({ ...prev, ...payment }));
        }
    }, [payment]);

    const handleInputChange = (field, value) => {
        const updatedSettings = {
            ...paymentSettings,
            [field]: value
        };
        setPaymentSettings(updatedSettings);
        setPayment(updatedSettings);
    };

    const handleGatewayToggle = (gateway) => {
        const field = `${gateway}_enabled`;
        handleInputChange(field, !paymentSettings[field]);
    };

    const PaymentGatewaySection = ({ title, gateways, description }) => (
        <div className="dc-tabsinfo dc-tabsinfo-mt">
            <div className="dc-tabscontenttitle">
                <h3>{title}</h3>
                <p className="text-muted">{description}</p>
            </div>
            <div className="dc-formtheme dc-userform">
                {gateways.map((gateway) => (
                    <div key={gateway.key} className="dc-tabsinfo dc-tabsinfo-mt">
                        <div className="form-group">
                            <div className="dc-checkbox-holder" style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '5px',
                                marginBottom: '10px'
                            }}>
                                <div>
                                    <label htmlFor={gateway.key}>
                                        <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                            {gateway.name}
                                        </span>
                                    </label>
                                    <p className="text-muted" style={{ margin: '5px 0 0 0', fontSize: '12px' }}>
                                        {gateway.description}
                                    </p>
                                </div>
                                <input
                                    type="checkbox"
                                    id={gateway.key}
                                    checked={paymentSettings[`${gateway.key}_enabled`] || false}
                                    onChange={() => handleGatewayToggle(gateway.key)}
                                    className="dc-checkbox"
                                    style={{ width: '20px', height: '20px' }}
                                />
                            </div>
                        </div>
                        
                        {paymentSettings[`${gateway.key}_enabled`] && (
                            <div className="row">
                                {gateway.fields.map((field) => (
                                    <div key={field.key} className="col-md-6">
                                        <div className="form-group">
                                            <label htmlFor={field.key}>{field.label}</label>
                                            <input
                                                type={field.type || 'text'}
                                                id={field.key}
                                                value={paymentSettings[field.key] || ''}
                                                onChange={(e) => handleInputChange(field.key, e.target.value)}
                                                placeholder={field.placeholder}
                                                className="form-control"
                                                required={field.required}
                                            />
                                            {field.help && (
                                                <small className="form-text text-muted">{field.help}</small>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="dc-tabscontent tab-content">
            <div className="dc-personalskillshold tab-pane active fade show" id="dc-skills">
                <div className="dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Payment Gateway Configuration</h3>
                        <p className="text-muted">Configure payment gateways for India and international transactions. Enable/disable payment methods and set up credentials.</p>
                    </div>
                </div>

                {/* General Settings */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h3>General Payment Settings</h3>
                    </div>
                    <div className="dc-formtheme dc-userform">
                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="currency">Currency</label>
                                    <select
                                        id="currency"
                                        value={paymentSettings.currency}
                                        onChange={(e) => handleInputChange('currency', e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="INR">Indian Rupee (₹)</option>
                                        <option value="USD">US Dollar ($)</option>
                                        <option value="EUR">Euro (€)</option>
                                        <option value="GBP">British Pound (£)</option>
                                        <option value="AED">UAE Dirham (د.إ)</option>
                                        <option value="SAR">Saudi Riyal (ر.س)</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="payment_mode">Payment Mode</label>
                                    <select
                                        id="payment_mode"
                                        value={paymentSettings.payment_mode}
                                        onChange={(e) => handleInputChange('payment_mode', e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="test">Test Mode Only</option>
                                        <option value="live">Live Mode Only</option>
                                        <option value="both">Both Test & Live</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="payment_timeout">Payment Timeout (minutes)</label>
                                    <input
                                        type="number"
                                        id="payment_timeout"
                                        value={paymentSettings.payment_timeout}
                                        onChange={(e) => handleInputChange('payment_timeout', parseInt(e.target.value))}
                                        className="form-control"
                                        min="5"
                                        max="60"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Indian Payment Gateways */}
                <PaymentGatewaySection
                    title="Indian Payment Gateways"
                    description="Configure payment gateways popular in India"
                    gateways={[
                        {
                            key: 'razorpay',
                            name: 'Razorpay',
                            description: 'Popular payment gateway for Indian businesses',
                            fields: [
                                { key: 'razorpay_key_id', label: 'Key ID', placeholder: 'rzp_test_...', required: true },
                                { key: 'razorpay_key_secret', label: 'Key Secret', type: 'password', placeholder: 'Enter secret key', required: true },
                                { key: 'razorpay_webhook_secret', label: 'Webhook Secret', type: 'password', placeholder: 'Enter webhook secret' }
                            ]
                        },
                        {
                            key: 'payu',
                            name: 'PayU',
                            description: 'Leading payment gateway in India',
                            fields: [
                                { key: 'payu_merchant_key', label: 'Merchant Key', placeholder: 'Enter merchant key', required: true },
                                { key: 'payu_merchant_salt', label: 'Merchant Salt', type: 'password', placeholder: 'Enter merchant salt', required: true },
                                { key: 'payu_auth_header', label: 'Auth Header', placeholder: 'Enter auth header' }
                            ]
                        },
                        {
                            key: 'paytm',
                            name: 'Paytm',
                            description: 'Digital wallet and payment gateway',
                            fields: [
                                { key: 'paytm_merchant_id', label: 'Merchant ID', placeholder: 'Enter merchant ID', required: true },
                                { key: 'paytm_merchant_key', label: 'Merchant Key', type: 'password', placeholder: 'Enter merchant key', required: true },
                                { key: 'paytm_website', label: 'Website', placeholder: 'WEBSTAGING or WEBPROD', required: true },
                                { key: 'paytm_industry_type', label: 'Industry Type', placeholder: 'Retail', required: true },
                                { key: 'paytm_channel_id', label: 'Channel ID', placeholder: 'WAP or WEB', required: true }
                            ]
                        },
                        {
                            key: 'ccavenue',
                            name: 'CCAvenue',
                            description: 'Multi-currency payment gateway',
                            fields: [
                                { key: 'ccavenue_merchant_id', label: 'Merchant ID', placeholder: 'Enter merchant ID', required: true },
                                { key: 'ccavenue_access_code', label: 'Access Code', placeholder: 'Enter access code', required: true },
                                { key: 'ccavenue_working_key', label: 'Working Key', type: 'password', placeholder: 'Enter working key', required: true }
                            ]
                        },
                        {
                            key: 'instamojo',
                            name: 'Instamojo',
                            description: 'Simple payment gateway for small businesses',
                            fields: [
                                { key: 'instamojo_api_key', label: 'API Key', placeholder: 'Enter API key', required: true },
                                { key: 'instamojo_auth_token', label: 'Auth Token', type: 'password', placeholder: 'Enter auth token', required: true },
                                { key: 'instamojo_salt', label: 'Salt', type: 'password', placeholder: 'Enter salt', required: true }
                            ]
                        }
                    ]}
                />

                {/* International Payment Gateways */}
                <PaymentGatewaySection
                    title="International Payment Gateways"
                    description="Configure international payment gateways for global transactions"
                    gateways={[
                        {
                            key: 'stripe',
                            name: 'Stripe',
                            description: 'Global payment processing platform',
                            fields: [
                                { key: 'stripe_publishable_key', label: 'Publishable Key', placeholder: 'pk_test_...', required: true },
                                { key: 'stripe_secret_key', label: 'Secret Key', type: 'password', placeholder: 'sk_test_...', required: true },
                                { key: 'stripe_webhook_secret', label: 'Webhook Secret', type: 'password', placeholder: 'whsec_...' }
                            ]
                        },
                        {
                            key: 'paypal',
                            name: 'PayPal',
                            description: 'Popular international payment method',
                            fields: [
                                { key: 'paypal_client_id', label: 'Client ID', placeholder: 'Enter client ID', required: true },
                                { key: 'paypal_client_secret', label: 'Client Secret', type: 'password', placeholder: 'Enter client secret', required: true },
                                { key: 'paypal_mode', label: 'Mode', type: 'select', options: [
                                    { value: 'sandbox', label: 'Sandbox' },
                                    { value: 'live', label: 'Live' }
                                ], required: true }
                            ]
                        },
                        {
                            key: 'square',
                            name: 'Square',
                            description: 'Payment processing for businesses',
                            fields: [
                                { key: 'square_application_id', label: 'Application ID', placeholder: 'Enter application ID', required: true },
                                { key: 'square_access_token', label: 'Access Token', type: 'password', placeholder: 'Enter access token', required: true },
                                { key: 'square_location_id', label: 'Location ID', placeholder: 'Enter location ID', required: true }
                            ]
                        }
                    ]}
                />

                {/* UPI and Digital Wallets */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h3>UPI & Digital Wallets</h3>
                        <p className="text-muted">Configure UPI and digital wallet payment methods</p>
                    </div>
                    <div className="dc-formtheme dc-userform">
                        <div className="row">
                            {[
                                { key: 'upi_enabled', name: 'UPI', description: 'Unified Payments Interface' },
                                { key: 'google_pay_enabled', name: 'Google Pay', description: 'Google payment app' },
                                { key: 'phonepe_enabled', name: 'PhonePe', description: 'PhonePe wallet' },
                                { key: 'paytm_wallet_enabled', name: 'Paytm Wallet', description: 'Paytm digital wallet' },
                                { key: 'amazon_pay_enabled', name: 'Amazon Pay', description: 'Amazon payment service' }
                            ].map((wallet) => (
                                <div key={wallet.key} className="col-md-6 col-lg-4">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <div>
                                            <label htmlFor={wallet.key}>
                                                <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                    {wallet.name}
                                                </span>
                                            </label>
                                            <p className="text-muted" style={{ margin: '5px 0 0 0', fontSize: '12px' }}>
                                                {wallet.description}
                                            </p>
                                        </div>
                                        <input
                                            type="checkbox"
                                            id={wallet.key}
                                            checked={paymentSettings[wallet.key] || false}
                                            onChange={() => handleInputChange(wallet.key, !paymentSettings[wallet.key])}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bank Transfer & COD */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h3>Bank Transfer & Cash on Delivery</h3>
                    </div>
                    <div className="dc-formtheme dc-userform">
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="bank_transfer_enabled">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Bank Transfer
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="bank_transfer_enabled"
                                            checked={paymentSettings.bank_transfer_enabled || false}
                                            onChange={() => handleInputChange('bank_transfer_enabled', !paymentSettings.bank_transfer_enabled)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                                {paymentSettings.bank_transfer_enabled && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="bank_name">Bank Name</label>
                                                <input
                                                    type="text"
                                                    id="bank_name"
                                                    value={paymentSettings.bank_name || ''}
                                                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="bank_account_number">Account Number</label>
                                                <input
                                                    type="text"
                                                    id="bank_account_number"
                                                    value={paymentSettings.bank_account_number || ''}
                                                    onChange={(e) => handleInputChange('bank_account_number', e.target.value)}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="bank_ifsc_code">IFSC Code</label>
                                                <input
                                                    type="text"
                                                    id="bank_ifsc_code"
                                                    value={paymentSettings.bank_ifsc_code || ''}
                                                    onChange={(e) => handleInputChange('bank_ifsc_code', e.target.value)}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="bank_account_holder">Account Holder Name</label>
                                                <input
                                                    type="text"
                                                    id="bank_account_holder"
                                                    value={paymentSettings.bank_account_holder || ''}
                                                    onChange={(e) => handleInputChange('bank_account_holder', e.target.value)}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="cod_enabled">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Cash on Delivery (COD)
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="cod_enabled"
                                            checked={paymentSettings.cod_enabled || false}
                                            onChange={() => handleInputChange('cod_enabled', !paymentSettings.cod_enabled)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                                {paymentSettings.cod_enabled && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="cod_minimum_amount">Minimum Amount (₹)</label>
                                                <input
                                                    type="number"
                                                    id="cod_minimum_amount"
                                                    value={paymentSettings.cod_minimum_amount || 0}
                                                    onChange={(e) => handleInputChange('cod_minimum_amount', parseFloat(e.target.value))}
                                                    className="form-control"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="cod_maximum_amount">Maximum Amount (₹)</label>
                                                <input
                                                    type="number"
                                                    id="cod_maximum_amount"
                                                    value={paymentSettings.cod_maximum_amount || 10000}
                                                    onChange={(e) => handleInputChange('cod_maximum_amount', parseFloat(e.target.value))}
                                                    className="form-control"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fee and Tax Configuration */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h3>Fee & Tax Configuration</h3>
                    </div>
                    <div className="dc-formtheme dc-userform">
                        <div className="row">
                            <div className="col-md-6">
                                <h5>Processing Fee</h5>
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="processing_fee_enabled">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Enable Processing Fee
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="processing_fee_enabled"
                                            checked={paymentSettings.processing_fee_enabled || false}
                                            onChange={() => handleInputChange('processing_fee_enabled', !paymentSettings.processing_fee_enabled)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                                {paymentSettings.processing_fee_enabled && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="processing_fee_type">Fee Type</label>
                                                <select
                                                    id="processing_fee_type"
                                                    value={paymentSettings.processing_fee_type}
                                                    onChange={(e) => handleInputChange('processing_fee_type', e.target.value)}
                                                    className="form-control"
                                                >
                                                    <option value="percentage">Percentage</option>
                                                    <option value="fixed">Fixed Amount</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="processing_fee_value">
                                                    Fee Value {paymentSettings.processing_fee_type === 'percentage' ? '(%)' : '(₹)'}
                                                </label>
                                                <input
                                                    type="number"
                                                    id="processing_fee_value"
                                                    value={paymentSettings.processing_fee_value || 0}
                                                    onChange={(e) => handleInputChange('processing_fee_value', parseFloat(e.target.value))}
                                                    className="form-control"
                                                    min="0"
                                                    step={paymentSettings.processing_fee_type === 'percentage' ? '0.1' : '1'}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-6">
                                <h5>GST Configuration</h5>
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="gst_enabled">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Enable GST
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="gst_enabled"
                                            checked={paymentSettings.gst_enabled || false}
                                            onChange={() => handleInputChange('gst_enabled', !paymentSettings.gst_enabled)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                                {paymentSettings.gst_enabled && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="gst_rate">GST Rate (%)</label>
                                                <input
                                                    type="number"
                                                    id="gst_rate"
                                                    value={paymentSettings.gst_rate || 18}
                                                    onChange={(e) => handleInputChange('gst_rate', parseFloat(e.target.value))}
                                                    className="form-control"
                                                    min="0"
                                                    max="100"
                                                    step="0.1"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="cgst_rate">CGST Rate (%)</label>
                                                <input
                                                    type="number"
                                                    id="cgst_rate"
                                                    value={paymentSettings.cgst_rate || 9}
                                                    onChange={(e) => handleInputChange('cgst_rate', parseFloat(e.target.value))}
                                                    className="form-control"
                                                    min="0"
                                                    max="100"
                                                    step="0.1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Payment;
